import { BrainCircuit } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import MavisForecastRow from './MavisForecastRow';
import type { MavisOpportunityRow } from '../../types/mavis';

interface Props {
  opportunities: MavisOpportunityRow[];
  ownerMap: Map<string, string>;
  loading: boolean;
  onRowClick: (id: string) => void;
}

const COLUMNS = [
  { label: 'Account',     className: 'w-40' },
  { label: 'Opportunity', className: '' },
  { label: 'Stage',       className: 'w-28' },
  { label: 'Amount',      className: 'w-32 text-right' },
  { label: 'Close Date',  className: 'w-32' },
  { label: 'Forecast',    className: 'w-32' },
  { label: 'Fcst %',      className: 'w-16 text-right' },
  { label: 'Owner',       className: 'w-32' },
  { label: 'Updated',     className: 'w-20' },
];

export default function MavisForecastTable({ opportunities, ownerMap, loading, onRowClick }: Props) {
  return (
    <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr className="border-b border-app-line bg-app-canvas/60">
              {COLUMNS.map(col => (
                <th
                  key={col.label}
                  className={`px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider ${col.className}`}
                >
                  {col.label}
                </th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="py-20 text-center">
                  <LoadingSpinner size="md" />
                </td>
              </tr>
            ) : opportunities.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="py-20 text-center">
                  <BrainCircuit className="w-10 h-10 text-app-muted mx-auto mb-3" />
                  <p className="text-sm font-medium text-app-muted mb-1">No opportunities found</p>
                  <p className="text-xs text-app-muted">
                    Try adjusting your filters, or create a new opportunity using the button above.
                  </p>
                </td>
              </tr>
            ) : (
              opportunities.map(opp => (
                <MavisForecastRow
                  key={opp.id}
                  opportunity={opp}
                  ownerName={opp.owner_user_id ? (ownerMap.get(opp.owner_user_id) ?? null) : null}
                  onClick={() => onRowClick(opp.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && opportunities.length > 0 && (
        <div className="px-4 py-2.5 border-t border-app-line bg-app-canvas/40">
          <p className="text-xs text-app-muted">
            {opportunities.length} {opportunities.length === 1 ? 'opportunity' : 'opportunities'}
          </p>
        </div>
      )}
    </div>
  );
}
